import { Link } from "react-router";
import { formatPhone } from "../../lib/format";
import {
  maskStorePhone,
  replyUsesDifferentStorePhoneLine,
  storePhoneLineStatus,
  verifiedStorePhoneLineNumber,
  type StorePhoneLineStatus,
} from "../../../shared/store-phone-line";

export const PHONE_MANAGER_URL = "/admin/inbound-messages?phoneManager=1";

export function phoneLineIsBlocked(
  registeredPhoneNumber: string | null | undefined,
  reportedPhoneNumber: string | null | undefined,
  manuallyVerifiedPhoneNumber?: string | null,
): boolean {
  const status = storePhoneLineStatus(
    registeredPhoneNumber,
    reportedPhoneNumber,
    manuallyVerifiedPhoneNumber,
  );
  return status !== "match" && status !== "manually_verified";
}

function shownPhone(value: string | null | undefined, fallback: string): string {
  return value ? formatPhone(value) || value : fallback;
}

/** 새 문자 카드·답장 창·먼저 보내는 창이 함께 쓰는 번호 대조 표시. */
export default function StorePhoneLineStatusView({
  mode,
  registeredPhoneNumber,
  reportedPhoneNumber,
  manuallyVerifiedPhoneNumber,
  replyOriginPhoneNumber,
  fallbackName = "매장폰",
  showPrimaryLine = true,
  onOpenPhoneManager,
}: {
  mode: "reply" | "compose";
  registeredPhoneNumber: string | null | undefined;
  reportedPhoneNumber: string | null | undefined;
  manuallyVerifiedPhoneNumber?: string | null;
  /** 답장할 때 원래 문자를 받은 번호. 선택폰 회선 확인과 분리해 빨간 안내에만 쓴다. */
  replyOriginPhoneNumber?: string | null;
  fallbackName?: string | null;
  /** 발송폰 선택 버튼이 이미 `발신번호:`를 그린 compose 창은 경고만 재사용한다. */
  showPrimaryLine?: boolean;
  onOpenPhoneManager?: () => void;
}) {
  const fallback = fallbackName?.trim() || "매장폰";
  const status: StorePhoneLineStatus = storePhoneLineStatus(
    registeredPhoneNumber,
    reportedPhoneNumber,
    manuallyVerifiedPhoneNumber,
  );
  const registered = shownPhone(registeredPhoneNumber, fallback);
  const reported = shownPhone(reportedPhoneNumber, "확인 안 됨");
  const sendingPhone = status === "manually_verified" ? registered : reported;
  const verifiedSendingPhone = verifiedStorePhoneLineNumber(
    registeredPhoneNumber,
    reportedPhoneNumber,
    manuallyVerifiedPhoneNumber,
  );
  const replyingFromDifferentPhone = replyUsesDifferentStorePhoneLine(
    replyOriginPhoneNumber,
    registeredPhoneNumber,
    reportedPhoneNumber,
    manuallyVerifiedPhoneNumber,
  );
  const differentReplyPhone = maskStorePhone(
    status === "mismatch" ? reportedPhoneNumber : verifiedSendingPhone,
  );

  return (
    <div data-testid="store-phone-line-status" data-phone-line-status={status} className="space-y-1.5">
      {showPrimaryLine && mode === "reply" ? (
        <div className="space-y-0.5 text-sm font-semibold text-gray-700 dark:text-gray-200">
          <p>메시지 수신번호 {registered}</p>
          <p>
            답장 발신번호 {status === "unknown" ? "확인 안 됨" : sendingPhone}
            {status === "match" ? (
              <span className="ml-1.5 text-emerald-600 dark:text-emerald-400">✓ 일치</span>
            ) : status === "manually_verified" ? (
              <span className="ml-1.5 text-emerald-600 dark:text-emerald-400">✓ 대표 확인</span>
            ) : null}
          </p>
        </div>
      ) : null}
      {showPrimaryLine && mode === "compose" ? (
        <p className="text-[18px] font-semibold leading-tight text-gray-700 dark:text-gray-200">
          발신번호: {status === "unknown" ? fallback : sendingPhone}
        </p>
      ) : null}

      {status === "mismatch" || replyingFromDifferentPhone ? (
        <p className="rounded-lg bg-rose-50 px-2.5 py-2 text-xs font-semibold text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
          {`⚠ 답장이 다른 번호(${differentReplyPhone})로 나갑니다`}
        </p>
      ) : null}
      {status === "unknown" ? (
        <div className="rounded-lg bg-amber-50 px-2.5 py-2 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
          <p>
            이 폰 번호가 확인되기 전까지는 답장을 보낼 수 없어요. 폰에 폰보드 앱을 새로
            설치하고 번호 확인을 허용해 주세요.
          </p>
          <Link
            to={PHONE_MANAGER_URL}
            onClick={onOpenPhoneManager}
            className="mt-2 inline-flex rounded-lg bg-amber-600 px-2.5 py-1.5 font-semibold text-white hover:bg-amber-700"
          >
            폰 관리 열기
          </Link>
        </div>
      ) : null}
    </div>
  );
}
